import type ISearchResult from './search-result';

/**
 * The SearchAutoCompleteEvent is dispatched when the user picks an item in the SearchAutoComplete popover, and when they leave its InputField.
 *
 * The InputField's own events never leave the component: it is a popover container that happens to hold one, and a listener above it has no business hearing from a child it does not know about. These two are what the component says about itself instead.
 *
 * The item type defaults to {@link ISearchResult}, so a listener that only reads the key and the name needs no type argument. One that wants the concrete class back — the ConstructionModel, the lookup value — names it.
 */
export default class SearchAutoCompleteEvent<Item extends ISearchResult = ISearchResult> extends Event {
	/**
	 * The static BLURRED type is dispatched when the user leaves the InputField.
	 *
	 * Dispatched whether or not an item was picked, which is the point of it: someone who knows the key types it and tabs straight on, and a listener that judges what the field holds has to hear about that too.
	 *
	 * Example: addEventListener(SearchAutoCompleteEvent.BLURRED, (event: SearchAutoCompleteEvent) => {});
	 */
	public static BLURRED = 'SearchAutoCompleteEvent.blurred';
	/**
	 * The static VALUE_CHANGED type is dispatched when the value the field holds has changed and the user is done changing it.
	 *
	 * Both ways of getting there raise it, and that is the whole reason it is one type rather than two: picking from the popover, and typing a key straight in and leaving. A separate event for the pick would have to be answered alongside this one by every listener that cares about the value, and would say nothing this
	 * does not — the item is right here when there was one.
	 *
	 * It carries the item when one was picked and null when the key was typed, so a listener that needs the item back has to say what it does with a key it was handed instead. The key is on `text` either way.
	 *
	 * Raised once per change: leaving a field nobody touched says nothing, and neither does leaving one that was put back the way it was found. It is never raised for a new search answer replacing the data, which is a thing the component did rather than a thing the user did.
	 *
	 * Example: addEventListener(SearchAutoCompleteEvent.VALUE_CHANGED, (event: SearchAutoCompleteEvent) => {});
	 */
	public static VALUE_CHANGED = 'SearchAutoCompleteEvent.valueChanged';
	/**
	 * The item the user selected, or null when they picked none.
	 *
	 * A VALUE_CHANGED carries one when the change was a pick and null when the key was typed. A BLURRED says nothing about what is picked, since leaving the field is not picking.
	 */
	public get selectedItem(): Item | null {
		return this.#selectedItem;
	}

	/**
	 * What the InputField held when this was raised.
	 *
	 * A record of the moment rather than something to act on later: the component's own `text` is what it holds now, and that is what a listener should read when it comes to judge.
	 */
	public get text(): string {
		return this.#text;
	}

	#selectedItem: Item | null;

	#text: string;

	public constructor(type: string, text: string, selectedItem: Item | null = null) {
		super(type, { bubbles: true });
		this.#selectedItem = selectedItem;
		this.#text = text;
	}
}
